import {
  Code2,
  Smartphone,
  Music,
  GaugeCircle,
  Users,
  PhoneCall,
  Route as RouteIcon,
  Workflow,
  BarChart3,
  Settings,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  route: string;
  label: string;
}

export interface NavGroup {
  label: string;
  Icon: LucideIcon;
  items: readonly NavItem[];
}

/**
 * Sidebar structure.
 *
 * Grouped the way a PBX admin already expects from established PBX admin UIs — status, then the things
 * that terminate calls, then the things that route them, then reports and system — so the mental
 * model transfers rather than having to be learned.
 *
 * Only screens that exist are listed. A nav item that opens an empty page teaches admins to
 * distrust the menu, which is worse than a shorter one.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Status Center',
    Icon: GaugeCircle,
    items: [
      { route: 'dashboard', label: 'Dashboard' },
      { route: 'active-calls', label: 'Active Calls' },
    ],
  },
  {
    label: 'Extensions and Trunks',
    Icon: Users,
    items: [
      { route: 'extensions', label: 'Extensions' },
      { route: 'extension-groups', label: 'Extension Groups' },
      { route: 'trunks', label: 'Trunks' },
    ],
  },
  {
    label: 'Softphone',
    Icon: PhoneCall,
    items: [{ route: 'softphone', label: 'Softphone' }],
  },
  {
    label: 'Auto Provisioning',
    Icon: Smartphone,
    items: [{ route: 'devices', label: 'Phones' }],
  },
  {
    label: 'Call Control',
    Icon: RouteIcon,
    items: [
      { route: 'inbound-routes', label: 'Inbound Route' },
      { route: 'outbound-routes', label: 'Outbound Route' },
      { route: 'time-conditions', label: 'Business Hours' },
    ],
  },
  {
    label: 'Call Features',
    Icon: Workflow,
    items: [
      { route: 'flows', label: 'Call Flows' },
      { route: 'call-features', label: 'Parking and Conferences' },
      { route: 'feature-codes', label: 'Feature Codes' },
      { route: 'recording', label: 'Recording' },
    ],
  },
  {
    label: 'Prompts',
    Icon: Music,
    items: [{ route: 'prompts', label: 'Prompts and Music' }],
  },
  {
    label: 'Reports',
    Icon: BarChart3,
    items: [
      { route: 'history', label: 'CDR' },
      { route: 'reports', label: 'Call Reports' },
      { route: 'recordings', label: 'Recordings and Voicemail' },
    ],
  },
  {
    label: 'Integrations',
    Icon: Code2,
    items: [
      { route: 'sdk-credentials', label: 'API Credentials' },
      { route: 'api-reference', label: 'API Reference' },
    ],
  },
  {
    label: 'System',
    Icon: Settings,
    items: [
      { route: 'settings', label: 'Settings' },
      { route: 'security', label: 'Security' },
      { route: 'storage', label: 'Storage' },
      { route: 'users', label: 'Users' },
      { route: 'system', label: 'Status and Configuration' },
      { route: 'events', label: 'Events' },
      { route: 'operation-log', label: 'Operation Log' },
      { route: 'maintenance', label: 'Maintenance' },
    ],
  },
] as const;

export const DEFAULT_ROUTE = 'dashboard';

export function isKnownRoute(route: string): boolean {
  return NAV_GROUPS.some((g) => g.items.some((i) => i.route === route));
}
